import type {
  ProjectPageProcessing,
  ProjectPageProcessingCounts,
  ProjectPageProcessingStage,
  SupervisorRun,
} from '@avisoma/shared';
import { CollapsibleCard } from './CollapsibleCard';
import { Link } from './Link';
import { SupervisorStageRow } from './SupervisorStageRow';
import { formatNumber } from '../lib/numberFormat';

const DEFAULT_STORAGE_KEY = 'projectView.collapsible.pageProcessing';

type StageKey = ProjectPageProcessingStage | 'reconstruction';

const STAGES: { key: StageKey; label: string }[] = [
  { key: 'pages', label: 'Pages' },
  { key: 'pdfParser', label: 'PDF parser' },
  { key: 'metadata', label: 'Metadata' },
  { key: 'embeddings', label: 'Embeddings' },
  { key: 'classification', label: 'Classification' },
  { key: 'reconstruction', label: 'Reconstruction' },
  { key: 'dataExtraction', label: 'Data extraction' },
];

/** Stages counted over documents — excluded from the page-denominated summary math. */
const DOCUMENT_COUNTED_STAGES: StageKey[] = ['pages', 'reconstruction'];

type SegmentKey = 'done' | 'processing' | 'error' | 'skip';

const SEGMENT_CLASSES: Record<SegmentKey, string> = {
  done: 'bg-emerald-500',
  processing: 'bg-indigo-500 animate-pulse',
  error: 'bg-red-500',
  skip: 'bg-zinc-300 dark:bg-zinc-600',
};

const LEGEND: { key: SegmentKey; label: string; swatch: string }[] = [
  { key: 'done', label: 'done', swatch: 'bg-emerald-500' },
  { key: 'processing', label: 'processing', swatch: 'bg-indigo-500' },
  { key: 'error', label: 'error', swatch: 'bg-red-500' },
  { key: 'skip', label: 'skipped', swatch: 'bg-zinc-300 dark:bg-zinc-600' },
];

const BREAKDOWN_ORDER = ['done', 'skip', 'processing', 'pending', 'error'] as const;
const BREAKDOWN_LABEL: Record<(typeof BREAKDOWN_ORDER)[number], string> = {
  done: 'done',
  skip: 'skipped',
  processing: 'processing',
  pending: 'pending',
  error: 'error',
};

function rowTotal(counts: ProjectPageProcessingCounts): number {
  return counts.done + counts.processing + counts.error + counts.skip + counts.pending;
}

/**
 * Pages that have reached a terminal, non-error state for a stage: processed (`done`)
 * plus deliberately-skipped (`skip`). Skipped pages are not pending work — a stage that
 * skips most of its pages (e.g. data extraction only runs on some categories) is settled,
 * not stuck — so they count toward the numerator alongside `done`.
 */
function settled(counts: ProjectPageProcessingCounts): number {
  return counts.done + counts.skip;
}

function pct(n: number, total: number): number {
  if (total <= 0) return 0;
  return (n / total) * 100;
}

function stageFullyDone(counts: ProjectPageProcessingCounts): boolean {
  const total = rowTotal(counts);
  return total > 0 && settled(counts) === total;
}

export type PageProcessingSummaryProps = {
  processing: ProjectPageProcessing;
  collapsibleStorageKey?: string;
  /** When supplied, the per-stage error counts become links to this failed-pages view. */
  errorHref?: string;
  /**
   * When supplied, overrides `errorHref` for the dataExtraction row only. That
   * row's error count includes documents whose reconstruction failed (DR-3815) —
   * those never appear in the page-status list `errorHref` points at, so this
   * row needs a document-level destination instead.
   */
  dataExtractionErrorHref?: string;
  /**
   * The project's supervisor stamp. `null` or `undefined` (supervisor off, or no run
   * yet) hides the Supervisor row.
   */
  supervisorRun?: SupervisorRun | null;
  /** When supplied, the Supervisor row's findings count links here. */
  supervisorHref?: string;
};

export function PageProcessingSummary({
  processing,
  collapsibleStorageKey = DEFAULT_STORAGE_KEY,
  errorHref,
  dataExtractionErrorHref,
  supervisorRun,
  supervisorHref,
}: PageProcessingSummaryProps) {
  const { totalPages, stages } = processing;

  // `reconstruction` is optional (the entity view's aggregation has no such stage) — only render rows we have counts for.
  const renderedStages = STAGES.flatMap(({ key, label }) => {
    const counts = stages[key];
    return counts ? [{ key, label, counts }] : [];
  });

  // Document-counted stages are excluded — they count documents, while the summary's denominator is `totalPages`.
  // The "N pages" figure is the least-settled page stage (done + skip), so a stage that skips
  // most of its pages doesn't drag the summary below the pages that are actually finished.
  const minSettled = renderedStages.reduce<number | null>((acc, { key, counts }) => {
    if (DOCUMENT_COUNTED_STAGES.includes(key)) return acc;
    const total = rowTotal(counts);
    if (total === 0) return acc;
    const value = settled(counts);
    return acc === null ? value : Math.min(acc, value);
  }, null);

  const doneStagesCount = renderedStages.reduce(
    (acc, { counts }) => acc + (stageFullyDone(counts) ? 1 : 0),
    0,
  );

  const summary =
    totalPages > 0
      ? `${doneStagesCount} / ${renderedStages.length} stages · ${formatNumber(minSettled ?? 0)} / ${formatNumber(totalPages)} pages`
      : 'No pages';

  const showLegend = renderedStages.some(({ counts }) => {
    return counts.processing > 0 || counts.error > 0 || counts.skip > 0;
  });

  return (
    <CollapsibleCard storageKey={collapsibleStorageKey} title="Page processing" summary={summary} defaultExpanded>
      {totalPages === 0 && processing.totalDocuments === 0 ? (
        <p className="px-1 py-2 text-xs text-gray-500 dark:text-zinc-500">No pages or documents linked yet.</p>
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            {renderedStages.map(({ key, label, counts }) => {
              const total = rowTotal(counts);
              const hasAny = total > 0;
              // Document-counted stages (pages, reconstruction) tally document-level
              // errors — the page-status filter view can't show those, so don't link.
              const errorLink = DOCUMENT_COUNTED_STAGES.includes(key)
                ? undefined
                : key === 'dataExtraction'
                  ? (dataExtractionErrorHref ?? errorHref)
                  : errorHref;
              const errorBadge =
                counts.error > 0 ? (
                  errorLink ? (
                    <Link
                      to={errorLink}
                      className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-semibold"
                    >
                      {' · '}
                      {formatNumber(counts.error)} err
                    </Link>
                  ) : (
                    <span className="text-red-600 dark:text-red-400 font-semibold">
                      {' · '}
                      {formatNumber(counts.error)} err
                    </span>
                  )
                ) : null;
              // Hover breakdown: the numerator now folds skip into done, so spell out the split.
              const breakdown = BREAKDOWN_ORDER.filter((seg) => counts[seg] > 0)
                .map((seg) => `${formatNumber(counts[seg])} ${BREAKDOWN_LABEL[seg]}`)
                .join(' · ');
              return (
                <div
                  key={key}
                  className={`grid grid-cols-[110px_1fr_96px] items-center gap-2 text-xs ${hasAny ? '' : 'opacity-60'}`}
                >
                  <span className="text-gray-700 dark:text-zinc-300">{label}</span>
                  <div className="flex h-2 overflow-hidden rounded bg-zinc-100 dark:bg-zinc-800">
                    {(['done', 'processing', 'error', 'skip'] as SegmentKey[]).map((seg) => {
                      const value = counts[seg];
                      if (value <= 0) return null;
                      return (
                        <div
                          key={seg}
                          className={SEGMENT_CLASSES[seg]}
                          style={{ width: `${pct(value, total)}%` }}
                        />
                      );
                    })}
                  </div>
                  <span
                    className="text-right tabular-nums text-gray-600 dark:text-zinc-400"
                    title={breakdown || undefined}
                  >
                    {hasAny ? (
                      <>
                        {formatNumber(settled(counts))} / {formatNumber(total)}
                        {errorBadge}
                      </>
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
              );
            })}
            <SupervisorStageRow run={supervisorRun} href={supervisorHref} />
          </div>
          {showLegend && (
            <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-gray-500 dark:text-zinc-500">
              {LEGEND.map(({ key, label, swatch }) => (
                <span key={key} className="inline-flex items-center gap-1">
                  <span className={`inline-block size-2 rounded-sm ${swatch}`} />
                  {label}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </CollapsibleCard>
  );
}
